import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ComparisonView, EliminateRequest, ReleaseHoldRequest, SelectedView } from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPost, type ApiError } from '../../../lib/api-client';

/**
 * S22 — DEC-02. The server's realtime hints name `comparison`, so a comparison already open
 * updates the moment someone else selects, holds or releases. Every change answers with
 * the whole comparison, which replaces the cache in one step.
 */

export const COMPARISON_KEY = ['comparison'] as const;

export function comparisonKey(jobId: string): readonly unknown[] {
  return [...COMPARISON_KEY, jobId];
}

export function useComparison(jobId: string): UseQueryResult<ComparisonView, ApiError> {
  return useQuery({
    queryKey: comparisonKey(jobId),
    queryFn: () => apiGet<ComparisonView>(`/jobs/${jobId}/comparison`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

function path(jobId: string, applicationId: string, action: string): string {
  return `/jobs/${jobId}/comparison/${applicationId}/${action}`;
}

export const comparisonApi = {
  add: (jobId: string, applicationId: string) => apiPost<ComparisonView>(path(jobId, applicationId, 'add'), {}),
  remove: (jobId: string, applicationId: string) => apiPost<ComparisonView>(path(jobId, applicationId, 'remove'), {}),
  eliminate: (jobId: string, applicationId: string, input: EliminateRequest) => apiPost<ComparisonView>(path(jobId, applicationId, 'eliminate'), input),
  restore: (jobId: string, applicationId: string) => apiPost<ComparisonView>(path(jobId, applicationId, 'restore'), {}),
  hold: (jobId: string, applicationId: string, version: number) => apiPost<ComparisonView>(path(jobId, applicationId, 'hold'), { version }),
  undoHold: (jobId: string, applicationId: string) => apiPost<ComparisonView>(path(jobId, applicationId, 'undo-hold'), {}),
  select: (jobId: string, applicationId: string, version: number) => apiPost<ComparisonView>(path(jobId, applicationId, 'select'), { version }),
  undoSelection: (jobId: string, applicationId: string) => apiPost<ComparisonView>(path(jobId, applicationId, 'undo-selection'), {}),
  release: (jobId: string, applicationId: string, input: ReleaseHoldRequest) => apiPost<ComparisonView>(path(jobId, applicationId, 'release'), input),
  extend: (jobId: string, applicationId: string) => apiPost<ComparisonView>(path(jobId, applicationId, 'extend'), {}),
};

// --- R8: JOB-12's ✓ Selected, and "Select" on the last round's result (F24.11) -----------------

/** Under `comparison`, so the same realtime hint refreshes it. */
export function selectedKey(jobId: string): readonly unknown[] {
  return [...COMPARISON_KEY, jobId, 'selected'];
}

export function useSelected(jobId: string, isEnabled = true): UseQueryResult<SelectedView, ApiError> {
  return useQuery({
    queryKey: selectedKey(jobId),
    queryFn: () => apiGet<SelectedView>(`/jobs/${jobId}/selected`),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export const selectionApi = {
  select: (jobId: string, applicationId: string, version: number) => apiPost<SelectedView>(`/jobs/${jobId}/selected`, { applicationId, version }),
  undo: (jobId: string, applicationId: string) => apiDelete<SelectedView>(`/jobs/${jobId}/selected/${applicationId}`),
};
